/**
 * The keybind card, as an object in the cockpit.
 *
 * It used to be a panel pinned to the top-right corner of the screen, which is
 * the one place a reference card does not belong: permanently in the way of the
 * sky, permanently in your peripheral vision, and gone the moment you switched
 * to the external camera.
 *
 * Now it is a card on the console in front of the seat, below the eyeline. You
 * look down to read it, exactly as you would in a real cockpit, and looking down
 * is already something the free-look control does. It costs nothing while you
 * are flying, because while you are flying you are not looking at it.
 *
 * Drawn to a canvas and mapped onto a plane. Text as geometry would need a font
 * loader and an atlas for eleven lines that never change.
 */

import * as THREE from 'three';

export interface ControlLine {
  key: string;
  action: string;
}

export interface ControlsCard {
  mesh: THREE.Mesh;
  dispose: () => void;
}

/**
 * Texture width, in pixels.
 *
 * Fixed, rather than derived from the card's size in the world. Those are
 * unrelated: the card is a few centimetres across and read from a few
 * centimetres away, so what decides whether the text is crisp is how many
 * texels it has, not how many world units it spans. Tying resolution to world
 * size is what made shrinking the card also make it illegible.
 */
const TEXTURE_WIDTH = 512;

/**
 * The layout, in fractions of the card's width.
 *
 * Everything is proportional so the aspect ratio holds however big the card is
 * drawn — the geometry and the canvas are generated from the same numbers, and
 * a card whose plane and texture disagreed would show the text stretched.
 */
const ROW = 1 / 12;
const PAD = 1 / 14;
/** Where the key column ends and the action column begins. */
const GUTTER = 0.34;

const PAPER = '#100a06';
const INK = '#ffd8bd';
const ACCENT = '#ff8a3d';

export function createControlsCard(lines: readonly ControlLine[], width: number): ControlsCard {
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_WIDTH;
  canvas.height = Math.round(TEXTURE_WIDTH * (PAD * 2 + lines.length * ROW));

  // Taken from the canvas rather than recomputed, so the plane and the texture
  // agree exactly. Rounding to whole pixels above otherwise leaves the two a
  // fraction of a percent apart, and the text is quietly stretched.
  const height = (width * canvas.height) / canvas.width;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    // One unit of the card's *width* in canvas pixels, so every measurement
    // below reads as the fraction it is.
    const u = TEXTURE_WIDTH;

    ctx.fillStyle = PAPER;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = Math.max(2, u * 0.008);
    ctx.strokeRect(
      ctx.lineWidth,
      ctx.lineWidth,
      canvas.width - ctx.lineWidth * 2,
      canvas.height - ctx.lineWidth * 2,
    );

    const font = ROW * u * 0.55;
    ctx.textBaseline = 'middle';

    lines.forEach((line, index) => {
      const y = (PAD + ROW * (index + 0.5)) * u;

      // The key on the left in the ship's own orange, the action beside it — so
      // the column you scan down is the one you are looking for.
      ctx.font = `bold ${font}px monospace`;
      ctx.fillStyle = ACCENT;
      ctx.textAlign = 'right';
      ctx.fillText(line.key, GUTTER * u, y);

      ctx.font = `${font}px monospace`;
      ctx.fillStyle = INK;
      ctx.textAlign = 'left';
      ctx.fillText(line.action, (GUTTER + 0.035) * u, y);
    });
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;

  const geometry = new THREE.PlaneGeometry(width, height);
  const material = new THREE.MeshBasicMaterial({ map: texture, toneMapped: false });

  return {
    mesh: new THREE.Mesh(geometry, material),
    dispose: () => {
      geometry.dispose();
      material.dispose();
      texture.dispose();
    },
  };
}
